"use client";

import {
  ActiveOrg,
  LocationPreferences,
  UserProfile,
} from "@/gen/ts/v1/user/user_service_pb";
import { User } from "firebase/auth";

import { createContext } from "react";
import { SignInResult, SignInWithGoogleResult } from "./api/sign-in.client";
import { SignUpUserWithPasswordResult } from "./api/sign-up.client";

import { getAuth } from "firebase/auth";
import { useMemo } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useUser } from "../users/hooks/use-user";
import { sendPasswordReset } from "./api/account.client";
import { refreshSession } from "./api/refresh.client";
import { signIn, signInWithGoogle } from "./api/sign-in.client";
import { signOut } from "./api/sign-out.client";
import { signUpUserWithPassword } from "./api/sign-up.client";
import { useUserSession } from "./hooks/use-user-session";

export function FirebaseAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, loading, error] = useAuthState(getAuth());

  const loaded = useMemo(() => !loading && !error, [loading, error]);

  const isAuthenticated = useMemo(() => Boolean(user), [user]);

  const { data: userData, isLoading: userDataLoading } = useUser(
    user?.uid || "",
    Boolean(user),
  );

  // Sync user session with cookies
  useUserSession(user ?? null);

  const contextValue = {
    activeOrg: userData?.userProfile?.activeOrg ?? null,
    signIn,
    signUpUserWithPassword,
    isAuthenticated,
    isLoading: loading || userDataLoading,
    loaded,
    signInWithGoogle,
    signOut,
    refreshSession,
    sendPasswordReset,
    user: user ?? null,
    userProfile: userData?.userProfile ?? null,
  } satisfies AuthContextType;

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// Auth context type
export interface AuthContextType {
  activeOrg: ActiveOrg | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loaded: boolean;
  refreshSession: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signIn: ({
    email,
    password,
    redirectUrl,
  }: {
    email: string;
    password: string;
    redirectUrl?: string;
  }) => Promise<SignInResult>;
  signInWithGoogle: ({
    redirectUrl,
  }: {
    redirectUrl?: string;
  }) => Promise<SignInWithGoogleResult>;
  signOut: () => Promise<void>;
  signUpUserWithPassword: ({
    email,
    password,
    username,
    locationPreferences,
    redirectUrl,
  }: {
    email: string;
    password: string;
    username: string;
    locationPreferences: LocationPreferences;
    redirectUrl?: string;
  }) => Promise<SignUpUserWithPasswordResult>;
  user: User | null | undefined;
  userProfile: UserProfile | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);
